import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

/**
 * Editor for the three independent-automation schedules:
 *
 * * ``kind="auto_close"`` — one trigger time per weekday. The minute
 *   the configured time arrives on that weekday, if the gate is
 *   open, the system fires a close command.
 *
 * * ``kind="auto_open"`` — same shape as auto-close. Fires an open
 *   command (no-op if the gate is already open or hermetically
 *   locked).
 *
 * * ``kind="lock"`` — a start→end window per weekday. Inside the
 *   window the gate is hermetically locked; outside it, free.
 *   Supports midnight crossing: type a smaller "end" than "start"
 *   and the window is read as wrapping to the next day.
 *
 * All three editors:
 * - Show a master toggle at the top that maps to a setting on
 *   ``gate_settings`` (``auto_close_enabled`` / ``auto_open_enabled``
 *   / ``hermetic_lock_enabled``).
 * - Render seven day rows with the type-appropriate inputs.
 * - Save on the same button: settings toggle PUT + schedule PUT.
 *
 * Single-gate and group-level modes are supported; bulk is not yet.
 */

const DAYS = [
  { value: 0, label: 'ראשון' },
  { value: 1, label: 'שני' },
  { value: 2, label: 'שלישי' },
  { value: 3, label: 'רביעי' },
  { value: 4, label: 'חמישי' },
  { value: 5, label: 'שישי' },
  { value: 6, label: 'שבת' },
];

// Today's weekday in our Sunday=0 convention. JavaScript's
// ``Date.getDay()`` is already Sunday=0..Saturday=6, so we can use it
// directly — used to badge "היום" next to the matching row in the
// schedule grid, so an operator running a quick "lock from now until
// in five minutes" test can't accidentally configure the wrong day.
const TODAY_WEEKDAY = (new Date()).getDay();


// True iff the window wraps past midnight — ``end_time`` strictly
// earlier than ``start_time``. The enforcer reads this same condition
// to know to extend the window into the next day; we surface it in
// the UI so the operator sees what the system will do.
function crossesMidnight(entry) {
  if (!entry || !entry.start || !entry.end) return false;
  return entry.end < entry.start;
}


function sameStartAndEnd(entry) {
  if (!entry || !entry.start || !entry.end) return false;
  return entry.start === entry.end;
}


function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        position: 'relative',
        width: '42px',
        height: '24px',
        borderRadius: '12px',
        border: 'none',
        background: checked ? '#16a34a' : '#d1d5db',
        cursor: 'pointer',
        transition: 'background 0.15s',
        padding: 0,
      }}
    >
      <span style={{
        position: 'absolute',
        top: '2px',
        left: checked ? '20px' : '2px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'left 0.15s',
      }} />
    </button>
  );
}


// Per-kind endpoint suffixes + payload shapes.
const KIND_META = {
  auto_close: {
    title: 'סגירה אוטומטית לפי שעה',
    enabledKey: 'auto_close_enabled',
    endpoint: 'auto-close-schedule',
    shape: 'trigger',
    triggerLabel: 'שעת סגירה',
    triggerDefault: '22:00',
  },
  auto_open: {
    title: 'פתיחה אוטומטית לפי שעה',
    enabledKey: 'auto_open_enabled',
    endpoint: 'auto-open-schedule',
    shape: 'trigger',
    triggerLabel: 'שעת פתיחה',
    triggerDefault: '06:00',
  },
  lock: {
    title: 'נעילה הרמטית לפי חלון שעות',
    enabledKey: 'hermetic_lock_enabled',
    endpoint: 'lock-schedule',
    shape: 'window',
    triggerDefault: { start: '21:00', end: '06:00' },
  },
};


function buildEmptyState(kind) {
  const out = {};
  for (const d of DAYS) {
    out[d.value] = null;
  }
  return out;
}


function itemsToByDay(items, kind) {
  const out = buildEmptyState(kind);
  for (const it of (items || [])) {
    if (kind === 'lock') {
      out[it.day] = { start: it.start, end: it.end };
    } else {
      out[it.day] = { time: it.time };
    }
  }
  return out;
}


function byDayToItems(byDay, kind) {
  const out = [];
  for (const d of DAYS) {
    const entry = byDay[d.value];
    if (!entry) continue;
    if (kind === 'lock') {
      out.push({ day: d.value, start: entry.start, end: entry.end });
    } else {
      out.push({ day: d.value, time: entry.time });
    }
  }
  return out;
}


function AutomationScheduleEditor({
  kind,
  gateId = null,
  groupId = null,
  apiBase = 'customer',
  initialEnabled = false,
  onClose,
  onApplied,
}) {
  const meta = KIND_META[kind];
  if (!meta) {
    throw new Error(`unknown automation kind: ${kind}`);
  }
  const isGroup = groupId != null;
  const owner = isGroup ? `gate-groups/${groupId}` : `gates/${gateId}`;
  const scheduleUrl = `/api/${apiBase}/${owner}/${meta.endpoint}`;
  const settingsUrl = isGroup
    ? `/api/${apiBase}/gate-groups/${groupId}/settings`
    : `/api/${apiBase}/gates/${gateId}/settings`;

  const [enabled, setEnabled] = useState(!!initialEnabled);
  const [byDay, setByDay] = useState(buildEmptyState(kind));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(scheduleUrl)
      .then(res => {
        if (cancelled) return;
        setByDay(itemsToByDay(res.data.items, kind));
      })
      .catch(() => { if (!cancelled) setByDay(buildEmptyState(kind)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scheduleUrl, kind]);

  const toggleDay = (day) => {
    setByDay(prev => ({
      ...prev,
      [day]: prev[day] == null
        ? (kind === 'lock'
            ? { ...meta.triggerDefault }
            : { time: meta.triggerDefault })
        : null,
    }));
  };

  const updateTrigger = (day, time) => {
    setByDay(prev => ({ ...prev, [day]: { time } }));
  };

  const updateWindow = (day, field, value) => {
    setByDay(prev => ({
      ...prev,
      [day]: { ...(prev[day] || meta.triggerDefault), [field]: value },
    }));
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      // 1) Persist the master toggle on settings.
      await axios.put(settingsUrl, { [meta.enabledKey]: enabled });
      // 2) Persist the schedule rows.
      await axios.put(scheduleUrl, { items: byDayToItems(byDay, kind) });
      if (onApplied) onApplied();
      onClose && onClose();
    } catch (e) {
      setError(e?.response?.data?.error || 'שגיאת שמירה — נסה שוב');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.4)', zIndex: 110,
        }}
      />
      <div style={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        background: '#fff',
        borderRadius: '10px',
        padding: '1.25rem',
        width: 'min(460px, calc(100vw - 24px))',
        maxHeight: '90vh',
        overflowY: 'auto',
        zIndex: 111,
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ fontSize: '1.05rem', fontWeight: 600 }}>{meta.title}</div>
          <button
            onClick={onClose}
            aria-label="סגור"
            style={{ border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#6b7280' }}
          >
            ×
          </button>
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.7rem 0.85rem', borderRadius: '8px', background: '#f9fafb',
          marginBottom: '1rem',
        }}>
          <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>
            הפעל את הפיצ'ר
          </span>
          <ToggleSwitch checked={enabled} onChange={setEnabled} />
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>טוען...</div>
        ) : (
          <>
            {kind === 'lock' && (
              <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.5rem', lineHeight: 1.5 }}>
                לכל יום שתסמן, הגדר שתי שעות: השעה שבה הנעילה <strong>מתחילה</strong> והשעה שבה היא <strong>מסתיימת</strong>. אם שעת הסיום קטנה משעת ההתחלה (לדוגמה התחלה 21:00, סיום 06:00), המערכת תבין שמדובר בחלון שחוצה את חצות ויסתיים בבוקר שלמחרת.
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {DAYS.map(d => {
                const entry = byDay[d.value];
                const dayOn = entry != null;
                const isToday = d.value === TODAY_WEEKDAY;
                return (
                  <div key={d.value} style={{
                    display: 'flex',
                    flexDirection: meta.shape === 'window' && dayOn ? 'column' : 'row',
                    alignItems: meta.shape === 'window' && dayOn ? 'stretch' : 'center',
                    gap: '0.45rem',
                    padding: '0.55rem 0.7rem',
                    borderRadius: '8px',
                    background: dayOn ? '#f0fdf4' : '#f9fafb',
                    border: `1px solid ${dayOn ? '#bbf7d0' : '#e5e7eb'}`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', minWidth: '5.5rem' }}>
                        <input
                          type="checkbox"
                          checked={dayOn}
                          onChange={() => toggleDay(d.value)}
                        />
                        <span style={{ fontSize: '0.9rem', fontWeight: isToday ? 600 : 400 }}>{d.label}</span>
                      </label>
                      {isToday && (
                        <span style={{
                          fontSize: '0.7rem',
                          background: '#dbeafe',
                          color: '#1e40af',
                          padding: '0.1rem 0.45rem',
                          borderRadius: '999px',
                          fontWeight: 600,
                        }}>היום</span>
                      )}
                      {dayOn && meta.shape === 'trigger' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginInlineStart: 'auto' }}>
                          <span style={{ fontSize: '0.82rem', color: '#374151' }}>ב-</span>
                          <input
                            type="time"
                            value={entry.time}
                            onChange={(e) => updateTrigger(d.value, e.target.value)}
                            style={{ padding: '0.3rem 0.45rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                          />
                        </div>
                      )}
                    </div>
                    {dayOn && meta.shape === 'window' && (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '0.6rem',
                        marginTop: '0.15rem',
                      }}>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                          <span style={{ fontSize: '0.78rem', color: '#374151', fontWeight: 500 }}>
                            מתחילה ב-
                          </span>
                          <input
                            type="time"
                            value={entry.start}
                            onChange={(e) => updateWindow(d.value, 'start', e.target.value)}
                            style={{ padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                          />
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                          <span style={{ fontSize: '0.78rem', color: '#374151', fontWeight: 500 }}>
                            מסתיימת ב-
                          </span>
                          <input
                            type="time"
                            value={entry.end}
                            onChange={(e) => updateWindow(d.value, 'end', e.target.value)}
                            style={{ padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                          />
                        </label>
                        {crossesMidnight(entry) && (
                          <div style={{
                            gridColumn: '1 / -1',
                            fontSize: '0.75rem',
                            color: '#1e40af',
                            background: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            borderRadius: '6px',
                            padding: '0.35rem 0.55rem',
                          }}>
                            חלון שחוצה חצות — הנעילה תתחיל ב-{entry.start} ותסתיים למחרת בבוקר ב-{entry.end}.
                          </div>
                        )}
                        {sameStartAndEnd(entry) && (
                          <div style={{
                            gridColumn: '1 / -1',
                            fontSize: '0.75rem',
                            color: '#b45309',
                            background: '#fffbeb',
                            border: '1px solid #fde68a',
                            borderRadius: '6px',
                            padding: '0.35rem 0.55rem',
                          }}>
                            שעת התחלה ושעת סיום זהות — בחר שעות שונות.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {error && (
          <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.6rem' }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.45rem 0.9rem',
              border: 'none',
              background: 'transparent',
              color: '#6b7280',
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            ביטול
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '0.45rem 1rem',
              border: 'none',
              background: saving ? '#9ca3af' : '#111827',
              color: '#fff',
              borderRadius: '6px',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: saving ? 'wait' : 'pointer',
            }}
          >
            {saving ? 'שומר…' : 'שמור'}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default AutomationScheduleEditor;
